// ./src/hooks/useMembershipTypes.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import {
  MembershipType,
  MembershipTypeService,
  MembershipTypeFilter,
  MembershipTypeInput,
  MembershipTypeBulkInput,
  Pagination,
} from "@/utils/api";
import { SortItem, listPage } from "@/utils/api";
import { upsertBy, upsertManyBy, removeBy, removeManyBy } from "@/utils";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

// ============================================
// Types
// ============================================

export interface UseMembershipTypesOptions {
  /**
   * Page size for pagination.
   * Mount-time configuration — changing after mount has no effect.
   */
  pageSize?: number;

  /**
   * Filters applied on initial autoLoad and userId-change reloads.
   * Mount-time configuration — call list(newFilters) to change.
   */
  initialFilters?: Partial<MembershipTypeFilter>;

  /**
   * Sort applied on initial autoLoad and userId-change reloads.
   * Mount-time configuration — pass sort to list() to change.
   */
  initialSort?: SortItem[];

  /** Auto-load data on mount (and on userId change) */
  autoLoad?: boolean;
}

export interface UseMembershipTypesReturn {
  // Data state
  membershipType: MembershipType | null;
  membershipTypes: MembershipType[];
  loading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  // Pagination state
  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  /** True while a bulk status/duration/uses change is in flight. */
  isBulkUpdating: boolean;
  /** IDs currently being removed — supports many concurrent removals. */
  removingIds: Set<string>;
  /** True when `id` is being removed (or, with no id, when any removal is in flight). */
  isRemoving: (id?: string) => boolean;
  selectedMembershipType: MembershipType | null;

  // Filter and sort
  filters: Partial<MembershipTypeFilter>;
  sortBy: SortItem[];

  // Core operations
  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<MembershipTypeFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: MembershipTypeInput) => Promise<OpResult>;
  update: (id: string, params: MembershipTypeInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;
  /** Mass-change status, duration, and/or uses on many types at once. */
  bulkUpdate: (ids: string[], params: MembershipTypeBulkInput) => Promise<OpResult>;

  // Convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedMembershipType: (membershipType: MembershipType | null) => void;

  // Helper operations
  reset: () => void;

  // Lookup helpers
  getById: (id: string | undefined) => MembershipType | undefined;

  // Derived data
  activeItems: MembershipType[];
  totalCount: number;
  activeCount: number;
  hasItems: boolean;

  // Error handling
  lastError: OpResult | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_SORT: SortItem[] = [{ name: "name", order: "ASC" }];

// Keep this empty — send only fields the API's MembershipTypeFilter accepts.
const DEFAULT_FILTERS: Partial<MembershipTypeFilter> = {};

// ============================================
// Hook Implementation
// ============================================

export const useMembershipTypes = (
  options: UseMembershipTypesOptions = {}
): UseMembershipTypesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  // ── Mount-time configuration captured in refs ──
  const pageSizeRef = useRef(pageSize);
  // The page size of the LAST read: a refresh re-runs that read, so a WHOLE_SET read stays whole (#118).
  const lastPageSizeRef = useRef<number>(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  // Ref to track the last-queried filters/sort for loadMore/refresh
  const lastQueriedFilterRef = useRef<Partial<MembershipTypeFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  // Monotonically increasing call ID to detect stale list() responses
  const listCallIdRef = useRef(0);

  // Track previous userId for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Data state
  const [membershipType, setMembershipType] = useState<MembershipType | null>(null);
  const [membershipTypes, setMembershipTypes] = useState<MembershipType[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state — "No notes
   * yet", "No tasks yet" — before the skeleton and then the data. Reported across the whole event
   * space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters — tracks what was last queried
  const [filters, setFilters] = useState<Partial<MembershipTypeFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [selectedMembershipType, setSelectedMembershipType] =
    useState<MembershipType | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<OpResult | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => isLoaded && membershipTypes.length === 0,
    [isLoaded, membershipTypes.length]
  );

  // ==========================================
  // Removal tracking (supports many at once)
  // ==========================================

  const startRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));

      return next;
    });
  }, []);

  const stopRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));

      return next;
    });
  }, []);

  const isRemoving = useCallback(
    (id?: string): boolean => (id ? removingIds.has(id) : removingIds.size > 0),
    [removingIds]
  );

  // ==========================================
  // Helpers
  // ==========================================

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;

    setMembershipType(null);
    setMembershipTypes([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setIsBulkUpdating(false);
    setRemovingIds(new Set());
    setSelectedMembershipType(null);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  const get = useCallback(async (id: string): Promise<OpResult> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await MembershipTypeService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data as MembershipType[])[0];
        setMembershipType(data);
        setMembershipTypes((prev) => upsertBy(prev, data));
        setLastError(null);

        return result;
      }

      setLastError(result);
      setMembershipType(null);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      setMembershipType(null);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * List membership types.
   * Race-condition safe via listCallIdRef.
   */
  const list = useCallback(
    async (
      filterOverride: Partial<MembershipTypeFilter> = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      lastPageSizeRef.current = targetPageSize;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        setCurrentPage(1);
      }

      setLastError(null);

      // Store queried filters/sort in refs
      setFilters(filterOverride);
      lastQueriedFilterRef.current = filterOverride;

      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const result = await listPage(
          MembershipTypeService.list,
          filterOverride as MembershipTypeFilter,
          paginationParams,
          currentSortBy
        );

        // Guard stale responses
        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as MembershipType[])
            : [];

          if (isAppending) {
            setMembershipTypes((prev) => upsertManyBy(prev, newItems, "id", "end"));
          } else {
            setMembershipTypes(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);

          return result;
        }

        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setMembershipTypes([]);
        }

        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setMembershipTypes([]);
        }

        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  const getMany = useCallback(async (ids: string[]): Promise<OpResult> => {
    if (ids.length === 0) {
      return new OpResult();
    }

    setLoading(true);
    setLastError(null);

    try {
      const result = await MembershipTypeService.getMany(ids);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const loadedItems = Array.isArray(result.data)
          ? (result.data as MembershipType[])
          : [];
        setMembershipTypes((prev) => upsertManyBy(prev, loadedItems));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  // ==========================================
  // Mutation Operations
  // ==========================================

  const create = useCallback(
    async (params: MembershipTypeInput): Promise<OpResult> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await MembershipTypeService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = (result.data as MembershipType[])[0];
          setMembershipType(newItem);
          setMembershipTypes((prev) => upsertBy(prev, newItem));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  const update = useCallback(
    async (id: string, params: MembershipTypeInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Membership type ID is required for updating"
        );
        setLastError(result);

        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await MembershipTypeService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as MembershipType[])[0];
          setMembershipType(updatedItem);
          setMembershipTypes((prev) => upsertBy(prev, updatedItem));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Membership type ID is required for removal"
        );
        setLastError(result);

        return result;
      }

      startRemoving([id]);
      setLastError(null);

      try {
        const result = await MembershipTypeService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setMembershipType(null);
          setMembershipTypes((prev) => removeBy(prev, id));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving([id]);
      }
    },
    [startRemoving, stopRemoving]
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one membership type ID is required for removal"
        );
        setLastError(result);

        return result;
      }

      startRemoving(ids);
      setLastError(null);

      try {
        const result = await MembershipTypeService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setMembershipType(null);
          setMembershipTypes((prev) => removeManyBy(prev, ids));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving(ids);
      }
    },
    [startRemoving, stopRemoving]
  );

  // Mass-change status, duration, and/or uses across many types. The server
  // applies the same patch to every id and returns the updated rows, which we
  // merge into the local list so the change shows without a full refresh.
  const bulkUpdate = useCallback(
    async (ids: string[], params: MembershipTypeBulkInput): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one membership type must be selected"
        );
        setLastError(result);

        return result;
      }

      setIsBulkUpdating(true);
      setLastError(null);

      try {
        const result = await MembershipTypeService.bulkUpdate(ids, params);

        if (result.code === OP_RESULT_CODES.OK) {
          const updatedItems = Array.isArray(result.data)
            ? (result.data as MembershipType[])
            : [];
          setMembershipTypes((prev) => upsertManyBy(prev, updatedItems));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsBulkUpdating(false);
      }
    },
    []
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    const nextPage = currentPage + 1;

    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loading, loadingMore, hasMore, currentPage]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: lastPageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(refresh, isLoaded);

  // ==========================================
  // Lookup Helpers
  // ==========================================

  const getById = useCallback(
    (id: string | undefined): MembershipType | undefined => {
      if (!id) {
        return undefined;
      }

      return membershipTypes.find((item) => item.id === id);
    },
    [membershipTypes]
  );

  // ==========================================
  // Derived Data
  // ==========================================

  const activeItems = useMemo(
    () => membershipTypes.filter((item) => item.status === STATUSES.ACTIVE),
    [membershipTypes]
  );

  const totalCount = useMemo(() => membershipTypes.length, [membershipTypes]);

  const activeCount = useMemo(() => activeItems.length, [activeItems]);

  const hasItems = useMemo(() => activeItems.length > 0, [activeItems]);

  // ==========================================
  // Effects
  // ==========================================

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }

      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      membershipType,
      membershipTypes,
      loading,
      isLoaded,
      isEmpty,

      // Pagination
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,

      // Operations state
      isCreating,
      isUpdating,
      isBulkUpdating,
      removingIds,
      isRemoving,
      selectedMembershipType,

      // Filter and sort
      filters,
      sortBy,

      // Core operations
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      bulkUpdate,

      // Convenience methods
      loadMore,
      refresh,

      // UI state management
      setSelectedMembershipType,

      // Helper operations
      reset,

      // Lookup helpers
      getById,

      // Derived data
      activeItems,
      totalCount,
      activeCount,
      hasItems,

      // Error handling
      lastError,
      clearError,
    }),
    [
      membershipType,
      membershipTypes,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      isBulkUpdating,
      removingIds,
      isRemoving,
      selectedMembershipType,
      filters,
      sortBy,
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      bulkUpdate,
      loadMore,
      refresh,
      reset,
      getById,
      activeItems,
      totalCount,
      activeCount,
      hasItems,
      lastError,
      clearError,
    ]
  );
};
